import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { useCompanies } from '../../features/company/CompanyContext';
import { Card, EmptyState, Loading, ModuleHero, StatusPill } from '../../components/ui';
import { IconChevronRight, IconCopy, IconLock, IconPlus } from '../../components/NavIcons';
import { COMPANY_STATUS, JOURNEY, TRACK_TONE, formatDate, journeyIndex } from '../../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE, CARD, CARD_STYLE } from '../../lib/ui';
import type { CompanySummary } from '../../types/workspace';
import CompanyLogo from '../../components/CompanyLogo';

function JourneyBar({ status }: { status: string }) {
  const at = journeyIndex(status);
  const step = JOURNEY[Math.max(at, 0)];
  return (
    <div>
      <div className="flex gap-1" aria-hidden>
        {JOURNEY.map((s, i) => (
          <span key={s.key} className={`h-1.5 flex-1 rounded-full ${i < at ? 'bg-emerald-400' : i === at ? 'bg-[#d6006e]' : 'bg-[#fce7f3]'}`} />
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-[#b0708f]">
        Step {Math.max(at, 0) + 1} of {JOURNEY.length} · <span className="font-bold text-[#6b0030]">{step.label}</span>
      </p>
    </div>
  );
}

function CompanyCard({ c, active, onOpen }: { c: CompanySummary; active: boolean; onOpen: (to?: string) => void }) {
  const status = COMPANY_STATUS[c.status];
  return (
    <section
      className={`${CARD} mz-card-link group relative flex flex-col p-4 transition-shadow`}
      style={{ ...CARD_STYLE, ...(active ? { border: '1.5px solid #d6006e', boxShadow: '0 12px 36px rgba(214,0,110,0.14)' } : {}) }}
    >
      <button type="button" onClick={() => onOpen()} className="text-left" aria-label={`Open ${c.logicalName || c.name}`}>
        <div className="flex items-start gap-3">
          <CompanyLogo companyId={c.id} name={c.logicalName || c.name} hasLogo={c.hasLogo} className="h-12 w-12 rounded-xl" />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-black text-[#6b0030] group-hover:text-[#d6006e]">{c.logicalName || c.name}</h2>
            <p className="truncate text-xs text-[#b0708f]">
              {[c.workspaceLabel, c.targetLender && `Lender: ${c.targetLender}`].filter(Boolean).join(' · ') || c.legalName || 'Company workspace'}
            </p>
          </div>
          {active ? (
            <span className="rounded-full bg-[#d6006e] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Active</span>
          ) : (
            <IconChevronRight className="h-5 w-5 text-[#f9a8d4] group-hover:text-[#d6006e]" />
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {status && <StatusPill label={status.label} tone={status.tone} />}
          {c.classification ? (
            <StatusPill label={c.classification.type.label} tone={TRACK_TONE[c.classification.track.code]} />
          ) : (
            <StatusPill label="Company type missing" tone="rose" />
          )}
        </div>

        <div className="mt-3">
          <JourneyBar status={c.status} />
        </div>

        <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-[#fff5f8] px-2 py-1.5">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Rating</dt>
            <dd className="flex items-center justify-center gap-1 text-base font-black text-[#d6006e]">
              {c.ratingLocked ? (
                <>
                  <IconLock className="h-3.5 w-3.5" /> <span className="text-xs">Pay to view</span>
                </>
              ) : (
                c.latestReport?.grade || '—'
              )}
            </dd>
          </div>
          <div className="rounded-xl bg-[#fff5f8] px-2 py-1.5">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Documents</dt>
            <dd className="text-base font-black text-[#6b0030]">{c.documentCount}</dd>
          </div>
          <div className="rounded-xl bg-[#fff5f8] px-2 py-1.5">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Partners</dt>
            <dd className="text-base font-black text-[#6b0030]">{c.partnerCount}</dd>
          </div>
        </dl>
      </button>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#fce7f3] pt-3">
        <button type="button" onClick={() => onOpen()} className={`${BTN_PRIMARY} px-3 py-2 text-xs`} style={BTN_PRIMARY_STYLE}>
          Open company
        </button>
        <button type="button" onClick={() => onOpen('/company')} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
          Edit profile
        </button>
        <Link to={`/company/new?from=${c.id}`} className={BTN_SOFT} style={BTN_SOFT_STYLE} title="Same company, different bank or facility">
          <IconCopy className="h-3.5 w-3.5" /> Duplicate
        </Link>
        <span className="ml-auto text-[11px] text-[#c084a0]">Added {formatDate(c.createdAt)}</span>
      </div>
    </section>
  );
}

export default function MyCompanies() {
  const user = useAppSelector((s) => s.auth.user);
  const { companies, active, select, loading } = useCompanies();
  const navigate = useNavigate();

  if (loading) return <Loading />;

  const firstName = (user?.name || user?.email || '').split(/[\s@]/)[0];
  const open = (id: string, to = '/overview') => {
    if (id !== active?.id) select(id);
    navigate(to);
  };

  return (
    <div>
      <ModuleHero
        eyebrow={`Welcome${firstName ? `, ${firstName}` : ''}`}
        title="My companies"
        description="Submit as many companies as you need from one login — or one workspace per bank for the same company. Open a company to work through its journey: register, submit, rated, improve, ready, partners and funded."
        actions={
          <Link to="/company/new" className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE}>
            <IconPlus className="h-4 w-4" /> Add company
          </Link>
        }
      />

      {companies.length === 0 ? (
        <div className="mt-6">
          <EmptyState title="No companies yet" body="Register your first company to start the rating journey." cta={{ label: 'Register company', to: '/company/new' }} />
        </div>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {companies.map((c) => (
            <CompanyCard key={c.id} c={c} active={c.id === active?.id} onOpen={(to) => open(c.id, to)} />
          ))}
          <Link
            to="/company/new"
            className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#f9c0d8] p-5 text-center text-[#d6006e] transition-colors hover:bg-[#fff5f8]"
          >
            <IconPlus className="h-6 w-6" />
            <span className="text-sm font-bold">Add another company</span>
            <span className="text-xs text-[#b0708f]">Each company gets its own documents, rating and partners</span>
          </Link>
        </div>
      )}

      <Card className="mt-6" title="How the journey works">
        <ol className="grid gap-2 text-sm text-[#6b0030] sm:grid-cols-2 lg:grid-cols-4">
          <li>
            <span className="font-bold">1–2 · Register & submit</span>
            <span className="block text-xs text-[#b0708f]">Your profile and the documents for your country.</span>
          </li>
          <li>
            <span className="font-bold">3–4 · Rated & improve</span>
            <span className="block text-xs text-[#b0708f]">Your Micus analyst rates you and lists improvements (read only; rating fee unlocks the result).</span>
          </li>
          <li>
            <span className="font-bold">4 · Partners</span>
            <span className="block text-xs text-[#b0708f]">You and your analyst agree the company summary for lenders.</span>
          </li>
          <li>
            <span className="font-bold">5 · Funded</span>
            <span className="block text-xs text-[#b0708f]">Track partner submissions and conversations, then the funded numbers and Micus fee.</span>
          </li>
        </ol>
      </Card>
    </div>
  );
}
